import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { setTimezone } from './lib/format';
import { useMe, useSettings } from './lib/hooks';
import { MechanicApp } from './mechanic/MechanicApp';
import { AuditPage } from './pages/admin/AuditPage';
import { DevicesPage } from './pages/admin/DevicesPage';
import { EmailPage } from './pages/admin/EmailPage';
import { UsersPage } from './pages/admin/UsersPage';
import { VoidApprovalsPage } from './pages/admin/VoidApprovalsPage';
import { BoardPage } from './pages/BoardPage';
import { CustomerPage } from './pages/CustomerPage';
import { CustomersPage } from './pages/CustomersPage';
import { LoginPage } from './pages/LoginPage';
import { MachinePage } from './pages/MachinePage';
import { MachinesPage } from './pages/MachinesPage';
import { NewWorkOrderPage } from './pages/NewWorkOrderPage';
import { WorkOrderPage } from './pages/WorkOrderPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import { CutoverPage, LegacyTicketsPage } from './pages/LegacyPages';
import { ReportsPage } from './pages/ReportsPage';

export function App() {
  const location = useLocation();
  // The iPad app has its own PIN sign-in and works offline; it never loads the office app.
  if (location.pathname === '/m' || location.pathname.startsWith('/m/')) return <MechanicApp />;
  return <OfficeApp />;
}

function OfficeApp() {
  const me = useMe();
  const location = useLocation();
  const settings = useSettings(Boolean(me.data) && me.data?.role !== 'MECHANIC');

  useEffect(() => {
    if (settings.data) {
      setTimezone(settings.data.timezone);
      document.title = settings.data.appName;
    }
  }, [settings.data]);

  if (location.pathname === '/login') return <LoginPage />;
  if (me.isLoading) return <Loading />;
  if (!me.data) return <Navigate to="/login" replace />;

  if (me.data.role === 'MECHANIC') return <Navigate to="/m" replace />;

  const canApprove = me.data.role === 'ADMIN' || me.data.role === 'MANAGER';

  return (
    <Layout me={me.data}>
      <Routes>
        <Route path="/" element={<BoardPage me={me.data} />} />
        <Route path="/work-orders" element={<WorkOrdersPage />} />
        <Route path="/work-orders/new" element={<NewWorkOrderPage me={me.data} />} />
        <Route path="/work-orders/:id" element={<WorkOrderPage me={me.data} />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerPage />} />
        <Route path="/machines" element={<MachinesPage />} />
        <Route path="/machines/:id" element={<MachinePage />} />
        <Route path="/reports" element={<ReportsPage me={me.data} />} />
        <Route path="/legacy" element={<LegacyTicketsPage />} />
        <Route path="/legacy/cutover" element={<CutoverPage me={me.data} />} />
        {canApprove && <Route path="/admin/users" element={<UsersPage me={me.data} />} />}
        {canApprove && <Route path="/admin/voids" element={<VoidApprovalsPage me={me.data} />} />}
        {canApprove && <Route path="/admin/audit" element={<AuditPage />} />}
        {canApprove && <Route path="/admin/devices" element={<DevicesPage me={me.data} />} />}
        {canApprove && <Route path="/admin/email" element={<EmailPage me={me.data} />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
